'use client';

import { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft, ShieldAlert, Eye, Zap } from 'lucide-react';

interface WelcomeScreen {
  heading: string;
  icon: typeof ShieldAlert;
  body: React.ReactNode;
}

const SCREENS: WelcomeScreen[] = [
  {
    heading: 'What Is E&O Risk?',
    icon: ShieldAlert,
    body: (
      <>
        <p className="text-base">
          <strong>Errors &amp; Omissions (E&amp;O)</strong> is the #1 liability risk for insurance
          agencies. A single misstatement in a text message can trigger a costly lawsuit.
        </p>
        <p className="text-[15px]">
          Phrases like &ldquo;you&rsquo;re covered,&rdquo; &ldquo;your claim will be
          paid,&rdquo; or &ldquo;coverage starts today&rdquo; can create implied guarantees
          that expose your agency to legal action &mdash; even when your team means well.
        </p>
        <p className="text-[15px]">
          With SMS being instant and informal, these slips happen more often than you&rsquo;d
          think. Voley&rsquo;s E&amp;O Guardrails catch them before they become a problem.
        </p>
      </>
    ),
  },
  {
    heading: 'Supervision Without Micromanaging',
    icon: Eye,
    body: (
      <>
        <p className="text-base">
          Guardrails scan every outgoing message in real time against <strong>58+
          risk phrases</strong> across three severity levels:
        </p>
        <div className="flex flex-col gap-2 pl-2">
          <div className="flex items-center gap-3">
            <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700">HIGH</span>
            <span className="text-[15px]">Coverage guarantees, binding language, claim promises</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="px-2 py-0.5 rounded text-xs font-bold bg-purple-100 text-purple-700">MEDIUM</span>
            <span className="text-[15px]">Directive advice, timing commitments, legal statements</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700">LOW</span>
            <span className="text-[15px]">Absolute terms like &ldquo;always,&rdquo; &ldquo;never,&rdquo; &ldquo;guaranteed&rdquo;</span>
          </div>
        </div>
        <p className="text-[15px]">
          Warnings <strong>flag but never block</strong>. Your team stays empowered to make
          judgment calls while you get peace of mind that risky language is being caught.
        </p>
      </>
    ),
  },
  {
    heading: "Let's See It in Action",
    icon: Zap,
    body: (
      <>
        <p className="text-base">
          We&rsquo;ll walk you through exactly how guardrails work:
        </p>
        <div className="flex flex-col gap-3 pl-4">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">1</div>
            <span className="text-[15px]">Type a risky phrase and watch it get flagged in real time</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">2</div>
            <span className="text-[15px]">See how the built-in explanations coach your team</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">3</div>
            <span className="text-[15px]">Learn how client notes provide essential E&amp;O context</span>
          </div>
        </div>
      </>
    ),
  },
];

interface EoWelcomeModalProps {
  onComplete: () => void;
}

export function EoWelcomeModal({ onComplete }: EoWelcomeModalProps) {
  const [activeScreen, setActiveScreen] = useState(0);
  const isLastScreen = activeScreen === SCREENS.length - 1;

  const handleNext = useCallback(() => {
    if (isLastScreen) {
      onComplete();
    } else {
      setActiveScreen((prev) => prev + 1);
    }
  }, [isLastScreen, onComplete]);

  const handleBack = useCallback(() => {
    setActiveScreen((prev) => Math.max(0, prev - 1));
  }, []);

  const screen = SCREENS[activeScreen];
  const Icon = screen.icon;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" />

      {/* Modal */}
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
        <div
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 fade-in duration-300"
          role="dialog"
          aria-modal="true"
          aria-label="E&O Guardrails Tour Welcome"
        >
          {/* Content */}
          <div className="p-8">
            {/* Icon and Heading Row */}
            <div className="flex items-start gap-4 mb-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl shrink-0 bg-accent-blue/[0.08]">
                <Icon className="h-6 w-6 text-accent-blue" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-1 text-gray-900">{screen.heading}</h2>
              </div>
            </div>

            {/* Body */}
            <div className="text-left leading-relaxed mb-8 space-y-4 text-gray-600 ml-16">
              {screen.body}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              {activeScreen > 0 ? (
                <button
                  onClick={handleBack}
                  aria-label="Previous screen"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </button>
              ) : (
                <div />
              )}

              <button
                onClick={handleNext}
                aria-label={isLastScreen ? 'Start the tour' : 'Next screen'}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium text-white bg-accent-blue hover:brightness-110 transition-all shadow-sm hover:shadow-md"
              >
                {isLastScreen ? "Let's Go" : 'Next'}
                {!isLastScreen && <ChevronRight className="h-4 w-4" />}
              </button>
            </div>

            {/* Step dots */}
            <div className="flex justify-center gap-1.5 mt-5">
              {SCREENS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === activeScreen ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
